import React, { useEffect, useState } from 'react';
import {
    Box, Button, Card, Chip, Grid, IconButton, Switch, TextField, Typography
} from '@mui/material';
import { ArrowBack, Delete, FontDownload, Upload } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import Loading from '../../../../components/Loader/Loading';
import { GlobalFontServices } from './GlobalFontServices';
import { buildGlobalFontFaceCss, refreshGlobalFontCache } from './useGlobalFonts';

const FONT_EXTENSIONS = ['ttf', 'otf', 'woff', 'woff2'];

const getFontNameFromFile = fileName => fileName
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const GlobalFontManagement = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [fonts, setFonts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updatingFontId, setUpdatingFontId] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [fileInputKey, setFileInputKey] = useState(0);
    const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm({
        mode: 'onChange',
        defaultValues: { name: '', file: null }
    });

    useEffect(() => {
        dispatch({ type: 'menuSelect', payload: 'home-screen-fonts' });
    }, [dispatch]);

    const fetchFonts = async () => {
        setLoading(true);
        const response = await GlobalFontServices.list();
        if (response?.success === 1) setFonts(response.data || []);
        setLoading(false);
    };

    useEffect(() => { fetchFonts(); }, []);

    const onSubmit = async data => {
        setLoading(true);
        setUploadProgress(0);
        try {
            const response = await GlobalFontServices.upload(data, setUploadProgress);
            if (response?.success !== 1) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Font upload failed',
                    text: response?.message || 'Unable to upload the font.'
                });
                return;
            }

            await refreshGlobalFontCache();
            reset();
            setFileInputKey(currentKey => currentKey + 1);
            await fetchFonts();
            await Swal.fire('Success', 'Font uploaded successfully', 'success');
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Font upload failed',
                text: error?.response?.data?.message || error?.message || 'Unable to upload the font.'
            });
        } finally {
            setLoading(false);
            setUploadProgress(0);
        }
    };

    const handleStatus = async font => {
        setUpdatingFontId(font.id);
        try {
            const response = await GlobalFontServices.update(font.id, { isActive: !font.isActive });
            if (response?.success === 1) {
                await refreshGlobalFontCache();
                await fetchFonts();
                return;
            }

            await Swal.fire({
                icon: 'error',
                title: 'Status update failed',
                text: response?.message || 'Unable to update the font status.'
            });
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Status update failed',
                text: error?.message || 'Unable to update the font status.'
            });
        } finally {
            setUpdatingFontId(null);
        }
    };

    const handleDelete = async font => {
        const result = await Swal.fire({
            icon: 'warning',
            title: `Delete ${font.name}?`,
            text: 'Fonts currently used by a section or item cannot be deleted.',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            confirmButtonColor: '#d32f2f'
        });
        if (!result.isConfirmed) return;

        try {
            const response = await GlobalFontServices.remove(font.id);
            if (response?.success === 1) {
                await refreshGlobalFontCache();
                await fetchFonts();
                return;
            }

            await Swal.fire({
                icon: 'error',
                title: 'Delete failed',
                text: response?.message || 'Unable to delete the font.'
            });
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Delete failed',
                text: error?.message || 'Unable to delete the font.'
            });
        }
    };

    return (
        <>
            {loading && <Loading />}
            {fonts.length > 0 && <style>{buildGlobalFontFaceCss(fonts.filter(font => font.isActive))}</style>}
            <PageHeaderLayout headerTitle="Fonts">
                <Button size="small" variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/customer-app/home-screen')} sx={{ textTransform: 'none' }}>
                    Home Screen
                </Button>
            </PageHeaderLayout>

            <Card component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 2, mb: 2 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1.5 }}>Upload Font</Typography>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Font Name</Typography>
                        <Controller
                            name="name"
                            control={control}
                            rules={{ maxLength: { value: 100, message: 'Maximum 100 characters' } }}
                            render={({ field }) => (
                                <TextField {...field} fullWidth size="small" placeholder="Font Name" error={!!errors.name} helperText={errors.name?.message} />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Font File *</Typography>
                        <Controller
                            name="file"
                            control={control}
                            rules={{
                                required: 'Font file is required',
                                validate: file => {
                                    const extension = file?.name?.split('.').pop()?.toLowerCase();
                                    if (!FONT_EXTENSIONS.includes(extension)) return 'Upload TTF, OTF, WOFF, or WOFF2';
                                    return file.size <= 5 * 1024 * 1024 || 'Font must be 5 MB or less';
                                }
                            }}
                            render={({ field: { onChange, value, ...field } }) => (
                                <TextField
                                    key={fileInputKey}
                                    {...field}
                                    type="file"
                                    fullWidth
                                    size="small"
                                    error={!!errors.file}
                                    helperText={errors.file?.message || value?.name}
                                    inputProps={{ accept: '.ttf,.otf,.woff,.woff2' }}
                                    onChange={event => {
                                        const selectedFile = event.target.files?.[0] || null;
                                        onChange(selectedFile);
                                        setValue(
                                            'name',
                                            selectedFile ? getFontNameFromFile(selectedFile.name) : '',
                                            { shouldDirty: true, shouldValidate: true }
                                        );
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Button type="submit" fullWidth variant="contained" startIcon={<Upload />} sx={{ mt: { xs: 0, md: 2.4 }, height: 40, textTransform: 'none' }}>
                            {uploadProgress > 0 ? `Uploading ${uploadProgress}%` : 'Upload Font'}
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            <Grid container spacing={2}>
                {fonts.map(font => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={font.id}>
                        <Card variant="outlined" sx={{ p: 2, opacity: font.isActive ? 1 : 0.65 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>{font.name}</Typography>
                                    <Typography sx={{ fontSize: '0.68rem', color: '#667085', wordBreak: 'break-all' }}>{font.originalFileName}</Typography>
                                </Box>
                                <Chip size="small" label={font.fileFormat?.toUpperCase()} />
                            </Box>
                            <Box sx={{ my: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
                                <Typography sx={{ fontFamily: `'${font.name}', sans-serif`, fontSize: '1.1rem' }}>
                                    The quick brown fox 123
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Switch
                                        size="small"
                                        checked={font.isActive}
                                        disabled={updatingFontId === font.id}
                                        onChange={() => handleStatus(font)}
                                    />
                                    <Typography sx={{ fontSize: '0.72rem' }}>{font.isActive ? 'Active' : 'Inactive'}</Typography>
                                </Box>
                                <Box>
                                    <IconButton size="small" component="a" href={font.fileUrl} target="_blank" rel="noreferrer" title="Open font file">
                                        <FontDownload sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(font)} title="Delete font">
                                        <Delete sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>
                ))}
                {!loading && fonts.length === 0 && (
                    <Grid item xs={12}>
                        <Card sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">No fonts uploaded yet.</Typography>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export default GlobalFontManagement;
